import {
    useState,
    useEffect,
    ChangeEvent,
    Dispatch,
    SetStateAction
} from 'react';
import { useMutation } from '@apollo/client';
import { toast } from 'react-toastify';

import theme from '@styles/theme';
import { isEmail, onlyNumber } from '@utils/common';
import { validateSystemOperator as validateUserInfo } from '@utils/validation';
import { Mutation } from '@graphql/types';
import { UPDATE_USER_INFO } from '@graphql/mutations';
import { SEE_USER_DETAIL } from '@graphql/queries';
import { UpdateUserInfoProps } from '@pages/Member/Status/Detail';

import { FlexBox } from '@components/styled/StyledComponents';
import StyledModal from '@components/styled/StyledModal';
import StyledInput from '@components/styled/StyledInput';
import StyledButton from '@components/styled/StyledButton';

type UpdateUserModalProps = {
    isOpen: boolean;
    setIsOpen: Dispatch<SetStateAction<boolean>>;
    data: UpdateUserInfoProps;
};

const UpdateUserModal = ({ isOpen, setIsOpen, data }: UpdateUserModalProps) => {
    const [loading, setLoading] = useState(false);
    const [emailValMsg, setEmailValMsg] = useState('');
    const [inputs, setInputs] = useState<UpdateUserInfoProps>({
        userId: null,
        email: '',
        name: '',
        cellphone: ''
    });

    const [updateUserInfo] =
        useMutation<Pick<Mutation, 'updateUserInfo'>>(UPDATE_USER_INFO);

    useEffect(() => {
        if (data) {
            setInputs(data);
        }
    }, [data]);

    const onChange = (e: ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;

        if (name === 'email') {
            if (!isEmail(value))
                setEmailValMsg('유효한 메일주소를 입력해주세요.');
            else setEmailValMsg('');
        } else if (name === 'cellphone') {
            return setInputs((prev) => ({
                ...prev,
                [name]: onlyNumber(value)
            }));
        }

        setInputs((prev) => ({
            ...prev,
            [name]: value
        }));
    };

    const onSubmit = async () => {
        const { userId, email, name, cellphone } = inputs;
        if (!validateUserInfo(email, name, cellphone)) return;

        setLoading(true);

        try {
            await updateUserInfo({
                variables: {
                    userId,
                    email,
                    name,
                    cellphone
                },
                onCompleted: () => {
                    toast.success(`회원 정보를 수정했습니다.`);
                    setIsOpen(false);
                },
                refetchQueries: [SEE_USER_DETAIL]
            });
        } catch (err) {
            console.error(err);
        } finally {
            return setLoading(false);
        }
    };

    return (
        <StyledModal
            title="회원 정보 수정"
            isOpen={isOpen}
            closeBtnHandler={() => setIsOpen(false)}
            marginBottom={32}
        >
            <StyledInput
                name="email"
                title="이메일"
                value={inputs.email}
                onChange={onChange}
                placeholder="이메일 입력"
                description={emailValMsg}
                fontColor={theme.colors.activeOrange}
                margin="0 0 24px"
                COLUMN
                REQUIRED
            />
            <StyledInput
                name="name"
                title="이름"
                value={inputs.name}
                onChange={onChange}
                placeholder="이름 입력"
                margin="0 0 24px"
                COLUMN
                REQUIRED
            />
            <StyledInput
                name="cellphone"
                title="휴대폰번호"
                value={inputs.cellphone}
                onChange={onChange}
                maxLength={11}
                placeholder="휴대폰번호 입력"
                margin="0 0 24px"
                COLUMN
                REQUIRED
            />
            <FlexBox $gap={8} $justifyContent="flex-end" $margin="32px 0 0">
                <StyledButton
                    title="취소"
                    width={140}
                    bgColor={theme.colors.whiteColor}
                    fontColor={theme.colors.blackColor}
                    border={`${theme.colors.lightGrayBorder} 1px solid`}
                    onClick={() => setIsOpen(false)}
                />
                <StyledButton
                    title="수정"
                    width={140}
                    bgColor={
                        !data
                            ? theme.colors.blackColor
                            : theme.colors.pointGreen
                    }
                    onClick={onSubmit}
                    loading={loading}
                />
            </FlexBox>
        </StyledModal>
    );
};

export default UpdateUserModal;
